import { ArrowRight, ListTodo, type LucideIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import type { CalendarInboxData, InboxProject } from '../api/calendar.api';
import { messages } from '@/shared/constants/messages';
import { PROJECT_STATUS_LABELS } from '@/shared/constants/statuses';
import { buttonVariants } from '@/shared/components/ui/button';
import { daysSince, formatDayMonth } from '@/shared/lib/dates';
import { cn } from '@/shared/lib/utils';

interface CalendarInboxProps {
  data: CalendarInboxData;
}

/**
 * **PRECISA DE VOCÊ** e **ATUALIZAÇÕES** — a caixa de entrada
 * (spec-calendar-ux.md §5.1, decisão 95).
 *
 * Ela é o que faz o calendário virar superfície de trabalho em vez de tela de
 * consulta ([ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md) §6): se o
 * encaixe acontece aqui, **retomar** um projeto pertence a esta tela. O ciclo
 * *"pediram ajuste → eu vejo → eu ajusto → volta pra fila"* fecha sem sair
 * daqui — e nenhuma regra de esteira se mudou de lugar: o formulário e as RPCs
 * continuam em `features/projects`. **O calendário é a porta, não a oficina.**
 *
 * ⚠️ **Componente próprio, nada de `ProjectTable`.** Não é uma listagem de
 * projetos: é uma fila de pendências com um verbo por linha. A tabela do painel
 * não tem coluna de verbo, e não é isso que ela deve virar.
 *
 * **Vazia, ela não é renderizada** (quem decide é a página) — e isso é regra, não
 * economia: uma caixa permanentemente vazia ensina o olho a pular a região onde
 * a novidade vai aparecer, e no dia em que houver novidade ele pula de novo.
 */
export function CalendarInbox({ data }: CalendarInboxProps) {
  const navigate = useNavigate();
  const hasQueue = data.reviewQueueCount !== null && data.reviewQueueCount > 0;

  return (
    <div className="space-y-5">
      {data.pending.length > 0 || hasQueue ? (
        <section aria-label={messages.calendar.inboxPending} className="space-y-2">
          <SectionTitle icon={ListTodo} count={data.pending.length}>
            {messages.calendar.inboxPending}
          </SectionTitle>

          {/*
            Para quem decide, **uma linha só** — e a contenção é a decisão: a
            fila é uma tela inteira, com ordenação, tempo de espera e contexto.
            Reimplementá-la em 320px seria a terceira verdade sobre o mesmo
            assunto, e a que envelheceria primeiro.
          */}
          {hasQueue ? (
            <Link
              to="/analise"
              className={cn(
                'flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm font-medium',
                'transition-[background-color,transform] duration-fast ease-out active:scale-[0.99]',
                'hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
              )}
            >
              <span>{messages.calendar.reviewQueueLine(data.reviewQueueCount ?? 0)}</span>
              <ArrowRight aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
            </Link>
          ) : null}

          <ul className="stagger space-y-1">
            {data.pending.map((project) => (
              <li key={project.id}>
                <PendingRow project={project} onOpen={() => navigate(`/projetos/${project.id}`)} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.updates.length > 0 ? (
        <section aria-label={messages.calendar.inboxUpdates} className="space-y-2">
          <SectionTitle>{messages.calendar.inboxUpdates}</SectionTitle>
          <ul className="stagger space-y-0.5">
            {data.updates.map((project) => (
              <li key={project.id}>
                <Link
                  to={`/projetos/${project.id}`}
                  state={{ from: '/calendario' }}
                  className={cn(
                    'block rounded-md px-2 py-1.5',
                    'transition-[background-color,transform] duration-fast ease-out active:scale-[0.99]',
                    'hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                  )}
                >
                  <span className="block truncate text-sm">
                    <span className="font-medium">{project.title ?? 'Projeto sem título'}</span>{' '}
                    <span className="text-muted-foreground">
                      {PROJECT_STATUS_LABELS[project.status].toLocaleLowerCase('pt-BR')}
                    </span>
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {whenLabel(project.decided_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function SectionTitle({
  children,
  count,
  icon: Icon,
}: {
  children: React.ReactNode;
  count?: number;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="flex items-center gap-1.5 text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground">
        {Icon ? <Icon aria-hidden="true" className="size-3.5 shrink-0" /> : null}
        {children}
      </h2>
      {/* Zero não vira número: um "0" fixo ensina o olho a ignorar o lugar onde
          o número de verdade vai aparecer. */}
      {count !== undefined && count > 0 ? (
        <span className="tabular rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-900">
          {count}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Uma pendência: **título · "ajustes solicitados" · data · `Ajustar`**.
 *
 * `Ajustar` leva a `/calendario/:id/editar` no modo ajuste, com o pedido do
 * revisor dentro ([spec-project-adjustments.md](../../../../docs/spec-project-adjustments.md)).
 * O botão é um `Link` e não um `button` com `navigate()`: clicar com o meio,
 * abrir em outra aba e ver o endereço na barra de status são coisas que um
 * `<button>` perde em silêncio.
 *
 * A linha inteira também abre o projeto — mas em LEITURA, não em edição. Quem
 * foi devolvido às vezes quer só reler o que pediram antes de decidir mexer.
 *
 * Sem preenchimento amarelo: a cor da situação já está no crachá e na borda
 * esquerda, e o resto do box usa `hover:bg-accent` — a mesma superfície neutra
 * da seção de atualizações logo abaixo.
 */
function PendingRow({ project, onOpen }: { project: InboxProject; onOpen: () => void }) {
  return (
    <div
      className={cn(
        'rounded-md border-l-2 border-l-amber-500 px-2.5 py-2',
        'transition-colors duration-fast ease-out hover:bg-accent',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        className="block w-full truncate text-left text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {project.title ?? 'Projeto sem título'}
      </button>
      <p className="text-xs text-muted-foreground">
        {/* A situação por extenso, do jeito que o painel inteiro a chama. */}
        {PROJECT_STATUS_LABELS.changes_requested.toLocaleLowerCase('pt-BR')}
        {project.starts_at ? ` · ${formatDayMonth(project.starts_at)}` : null}
      </p>
      {/*
        Preenchido no mesmo `amber-500` da moldura do card (linha acima e
        `warning.filled` em `appearance.ts`) — o botão usa a cor da situação
        que ele resolve. Texto escuro (`amber-950`), não branco: branco sobre
        `amber-500` dá 2,2:1 e reprova no AA; `amber-950` dá ~7:1.
      */}
      <Link
        to={`/calendario/${project.id}/editar`}
        className={cn(
          buttonVariants({ variant: 'default', size: 'sm' }),
          'mt-2 h-7 w-fit px-2.5 text-xs',
          'bg-amber-500 text-amber-950 shadow-[0_1px_2px_rgb(245_158_11/0.24),0_2px_6px_-2px_rgb(245_158_11/0.30)]',
          'hover:bg-amber-600 hover:shadow-[0_1px_2px_rgb(245_158_11/0.30),0_5px_14px_-3px_rgb(245_158_11/0.38)]',
        )}
      >
        {messages.calendar.resolve}
      </Link>
    </div>
  );
}

/**
 * "hoje" · "ontem" · "há 3 dias".
 *
 * A janela é de 7 dias, então a contagem nunca passa disso e não precisa de
 * data absoluta — quem quiser a data abre o projeto.
 */
function whenLabel(decidedAt: string | null): string {
  if (!decidedAt) return '';
  const days = daysSince(decidedAt);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  return `há ${days} dias`;
}
